import React from "react";
import {
  Alert,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { gql, useMutation, useQuery } from "@apollo/client";

import {
  BookingProviderCapabilities,
  BookingProviderConfig,
  BookingProviderType,
  getBookingProviderConfig,
  isValidExternalBookingUrl,
} from "../../../modules/booking-provider";
import { useSelectedBusiness } from "../../../platform/redux/store";
import {
  GET_BOOKING_PROVIDER_CONNECTION,
  GET_BOOKING_PROVIDER_OPTIONS,
  SAVE_BOOKING_PROVIDER_CONNECTION,
  VALIDATE_BOOKING_PROVIDER_CONNECTION,
} from "../../../platform/graphql/queries/generic";
import {
  buildTimelyManagementUrl,
  buildTimelyPublicBookingUrl,
} from "../integrations/booking/timelyClient";

const BEGIN_TIMELY = gql`mutation BeginTimelyOwnerAuthorization($businessId:ID!,$returnUrl:String!){beginTimelyOwnerAuthorization(businessId:$businessId,returnUrl:$returnUrl)}`;
const COMPLETE_TIMELY = gql`mutation CompleteTimelyOwnerAuthorization($proof:String!){completeTimelyOwnerAuthorization(proof:$proof){id businessId providerType providerBusinessId providerBusinessUrl status}}`;

export default function CareSchedulePage(): JSX.Element {
  const selectedBusiness = useSelectedBusiness();
  const businessId = Number(selectedBusiness?.businessBasic?.id || 0);
  const businessUrl = String(selectedBusiness?.businessBasic?.url || "").trim();
  const [provider, setProvider] = React.useState<BookingProviderConfig>({ type: "TIMELY365" });
  const [externalUrl, setExternalUrl] = React.useState("");
  const [managementUrl, setManagementUrl] = React.useState("");
  const [providerResourceId, setProviderResourceId] = React.useState("");
  const [timelyBusinessId, setTimelyBusinessId] = React.useState("");
  const [timelyBusinessUrl, setTimelyBusinessUrl] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [savedConnection, setSavedConnection] = React.useState<any>(null);
  const [beginTimely, { loading: connecting }] = useMutation(BEGIN_TIMELY);
  const [completeTimely, { loading: completing }] = useMutation(COMPLETE_TIMELY);
  const completedProof = React.useRef(false);

  const connectionQuery = useQuery(GET_BOOKING_PROVIDER_CONNECTION, {
    variables: { businessId },
    skip: !businessId,
    fetchPolicy: "cache-and-network",
  });
  const optionsQuery = useQuery(GET_BOOKING_PROVIDER_OPTIONS);
  React.useEffect(() => {
    const proof = new URLSearchParams(window.location.hash.slice(1)).get("timelyConnection");
    if (!proof || completedProof.current || !businessId) return;
    completedProof.current = true;
    window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
    completeTimely({ variables: { proof } }).then(async result => {
      setSavedConnection(result.data?.completeTimelyOwnerAuthorization);
      await connectionQuery.refetch();
      setMessage("Booking provider saved for this workspace.");
    }).catch(error => setMessage(error.message || "Timely ownership could not be verified. Connect again."));
  }, [businessId, completeTimely, connectionQuery.refetch]);
  const [saveConnection, { loading: saving }] = useMutation(
    SAVE_BOOKING_PROVIDER_CONNECTION
  );
  const [validateConnection, { loading: validating }] = useMutation(
    VALIDATE_BOOKING_PROVIDER_CONNECTION
  );

  React.useEffect(() => {
    const connection = connectionQuery.data?.getBookingProviderConnection || null;
    setSavedConnection(connection);
    setProvider(getBookingProviderConfig({ bookingProvider: connection }, businessId));
    setExternalUrl(connection?.publicBookingUrl || connection?.externalUrl || "");
    setManagementUrl(connection?.managementUrl || "");
    setProviderResourceId(connection?.providerResourceId || "");
    setTimelyBusinessId(connection?.providerBusinessId || "");
    setTimelyBusinessUrl(connection?.providerBusinessUrl || "");
    setMessage("");
  }, [businessId, businessUrl, connectionQuery.data]);

  const saveProvider = async () => {
    if (!businessId) {
      setMessage("Select a business before configuring booking.");
      return;
    }
    if (
      new Set<BookingProviderType>(["EXTERNAL_URL", "CALENDLY"]).has(provider.type) &&
      !isValidExternalBookingUrl(externalUrl)
    ) {
      setMessage("Enter a valid HTTP or HTTPS booking URL.");
      return;
    }
    if (managementUrl && !isValidExternalBookingUrl(managementUrl)) {
      setMessage("Enter a valid HTTP or HTTPS management URL.");
      return;
    }
    try {
      if (provider.type === "TIMELY365") {
        const result = await beginTimely({ variables: { businessId, returnUrl: `${window.location.origin}/care/schedule` } });
        window.location.assign(result.data.beginTimelyOwnerAuthorization);
        return;
      }
      const saved = await saveConnection({
        variables: {
          input: {
            businessId,
            providerType: provider.type,
            externalUrl: provider.type === "EXTERNAL_URL" ? externalUrl.trim() : null,
            publicBookingUrl:
              new Set<BookingProviderType>([
                "EXTERNAL_URL",
                "CALENDLY",
                "GOOGLE_CALENDAR",
                "MICROSOFT_OUTLOOK",
              ]).has(provider.type)
                ? externalUrl.trim() || null
                : null,
            managementUrl: managementUrl.trim() || null,
            providerResourceId: providerResourceId.trim() || null,
            providerBusinessId: null,
            providerBusinessUrl: null,
            enabled: provider.type !== "NONE",
          },
        },
      });
      let connection = saved.data?.saveBookingProviderConnection;
      if (provider.type !== "MANUAL_REQUEST" && provider.type !== "NONE") {
        const validated = await validateConnection({ variables: { businessId } });
        connection = validated.data?.validateBookingProviderConnection || connection;
      }
      setSavedConnection(connection);
      if (connection?.status === "ERROR") {
        setMessage(connection.lastError || "The booking provider could not be reached from the Plany server.");
      } else {
        setMessage("Booking provider saved for this workspace.");
      }
      await connectionQuery.refetch();
    } catch (error: any) {
      setMessage(error?.message || "Booking provider could not be saved.");
    }
  };

  const activeConnection =
    savedConnection?.providerType === provider.type ? savedConnection : null;
  const timelyManagementUrl =
    activeConnection?.managementUrl || buildTimelyManagementUrl(timelyBusinessUrl);
  const timelyPublicBookingUrl =
    activeConnection?.publicBookingUrl || buildTimelyPublicBookingUrl(timelyBusinessUrl);
  const busy = saving || validating || connecting || completing;
  const providerOptions: BookingProviderCapabilities[] =
    (optionsQuery.data?.bookingProviderOptions || [
      { key: "TIMELY365", label: "Timely365" },
      { key: "EXTERNAL_URL", label: "External booking link" },
      { key: "NONE", label: "No online booking" },
    ] as BookingProviderCapabilities[]);
  const selectedCapabilities =
    activeConnection?.capabilities ||
    providerOptions.find((item) => item.key === provider.type);
  const usesProviderLinks = new Set<BookingProviderType>([
    "GOOGLE_CALENDAR",
    "MICROSOFT_OUTLOOK",
    "CALENDLY",
    "EXTERNAL_URL",
  ]).has(provider.type);

  return (
    <Stack spacing={2}>
      <Paper
        elevation={0}
        sx={{ p: 2.5, borderRadius: 4, border: "1px solid", borderColor: "divider" }}
      >
        <Stack spacing={1}>
          <Typography variant="h6" fontWeight={800}>
            Schedule Assessments, Tours, and Intake Visits
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose a booking service for this Plany workspace. Timely365 is optional;
            you can also use another provider's booking link.
          </Typography>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{ p: 2.5, borderRadius: 4, border: "1px solid", borderColor: "divider" }}
      >
        <Stack spacing={2}>
          <Typography variant="subtitle1" fontWeight={800}>
            Booking integration
          </Typography>
          <FormControl fullWidth>
            <InputLabel id="care-booking-provider-label">Booking provider</InputLabel>
            <Select
              labelId="care-booking-provider-label"
              label="Booking provider"
              value={provider.type}
              onChange={(event) => {
                setProvider({ type: event.target.value as BookingProviderType });
                setMessage("");
              }}
            >
              {providerOptions.map((option) => (
                <MenuItem key={option.key} value={option.key}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {provider.type === "TIMELY365" ? (
            <Stack spacing={2}>
              <TextField
                label="Timely business ID"
                value={timelyBusinessId}
                InputProps={{ readOnly: true }}
                helperText="Selected after you sign in to Timely and approve ownership."
                fullWidth
              />
              <TextField
                label="Timely booking page name"
                value={timelyBusinessUrl}
                InputProps={{ readOnly: true }}
                helperText="The name after /b/ in your Timely booking page address."
                fullWidth
              />
            </Stack>
          ) : null}
          {usesProviderLinks ? (
            <Stack spacing={2}>
              <TextField
                label={
                  provider.type === "EXTERNAL_URL"
                    ? "Public booking URL"
                    : `${selectedCapabilities?.label || "Provider"} booking URL`
                }
                value={externalUrl}
                onChange={(event) => setExternalUrl(event.target.value)}
                placeholder="https://example.com/book"
                helperText={
                  new Set<BookingProviderType>(["GOOGLE_CALENDAR", "MICROSOFT_OUTLOOK"]).has(provider.type)
                    ? "Optional until OAuth is connected. Plany owns the booking flow for calendar providers."
                    : undefined
                }
                fullWidth
              />
              <TextField
                label="Availability management URL"
                value={managementUrl}
                onChange={(event) => setManagementUrl(event.target.value)}
                placeholder="https://example.com/manage"
                helperText="Where the provider updates working hours and availability."
                fullWidth
              />
              <TextField
                label="Provider resource ID"
                value={providerResourceId}
                onChange={(event) => setProviderResourceId(event.target.value)}
                helperText="Calendar ID, event type URI, or provider-owned service identifier."
                fullWidth
              />
            </Stack>
          ) : null}
          <Button
            variant="contained"
            onClick={saveProvider}
            disabled={busy || connectionQuery.loading || !businessId}
            sx={{ alignSelf: "flex-start" }}
          >
            {busy ? "Connecting…" : provider.type === "TIMELY365" ? "Sign in to Timely to connect" : "Save booking provider"}
          </Button>
          {connectionQuery.error ? (
            <Alert severity="error">{connectionQuery.error.message}</Alert>
          ) : null}
          {message ? (
            <Alert severity={message === "Booking provider saved for this workspace." ? "success" : "error"}>
              {message}
            </Alert>
          ) : null}
        </Stack>
      </Paper>

      {selectedCapabilities?.availabilityMode && provider.type !== "NONE" ? (
        <Alert
          severity={
            activeConnection?.status === "CONNECTED"
              ? "success"
              : activeConnection?.status === "SETUP_REQUIRED"
                ? "warning"
                : "info"
          }
        >
          <Typography variant="subtitle2" fontWeight={800}>
            {selectedCapabilities.label}: {activeConnection?.status || "Not saved"}
          </Typography>
          {activeConnection?.lastError ? (
            <Typography variant="body2">{activeConnection.lastError}</Typography>
          ) : null}
        </Alert>
      ) : null}

      {provider.type === "TIMELY365" ? (
        <Paper
          elevation={0}
          sx={{ p: 2.5, borderRadius: 4, border: "1px solid", borderColor: "divider" }}
        >
          <Stack spacing={1.5}>
            <Typography variant="subtitle1" fontWeight={800}>
              Timely365 connected booking
            </Typography>
            {activeConnection?.status ? (
              <Alert severity={activeConnection.status === "CONNECTED" ? "success" : "warning"}>
                Server connection: {activeConnection.status}
                {activeConnection.lastError ? ` — ${activeConnection.lastError}` : ""}
              </Alert>
            ) : null}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Button
                href={timelyManagementUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                endIcon={<OpenInNewIcon />}
              >
                Open Timely calendar
              </Button>
              {timelyPublicBookingUrl ? (
                <Button
                  href={timelyPublicBookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  endIcon={<OpenInNewIcon />}
                >
                  Open public booking page
                </Button>
              ) : null}
            </Stack>
          </Stack>
        </Paper>
      ) : null}

      {provider.type === "EXTERNAL_URL" ? (
        <Alert
          severity="info"
          action={
            isValidExternalBookingUrl(externalUrl) ? (
              <Button color="inherit" href={externalUrl} target="_blank" rel="noopener noreferrer">
                Open booking
              </Button>
            ) : undefined
          }
        >
          Scheduling is handled by the connected external booking page.
        </Alert>
      ) : null}
      {new Set<BookingProviderType>([
        "GOOGLE_CALENDAR",
        "MICROSOFT_OUTLOOK",
        "CALENDLY",
      ]).has(provider.type) ? (
        <Alert severity={activeConnection?.status === "CONNECTED" ? "success" : "warning"}>
          {activeConnection?.status === "CONNECTED"
            ? `${selectedCapabilities?.label || "Provider"} is connected for automatic scheduling updates.`
            : `${selectedCapabilities?.label || "Provider"} is saved, but OAuth and change notifications must be connected before automatic booking is enabled.`}
        </Alert>
      ) : null}
      {provider.type === "MANUAL_REQUEST" ? (
        <Alert severity="warning">
          Manual booking requests are selected. The request workflow requires the next backend migration before it can accept requests.
        </Alert>
      ) : null}
      {provider.type === "NONE" ? (
        <Alert severity="info">Online scheduling is disabled for this Care workspace.</Alert>
      ) : null}
    </Stack>
  );
}
